import { useMemo, Fragment, ReactNode } from 'react';

import { FormFieldType } from 'types';

import { FormText } from './text';
import { FormHeading } from './heading';
import { FormInputField } from './input';
import { FormRadioField } from './radio';
import { FormSelectField } from './select';
import { FormCheckboxField } from './checkbox';
import { FieldArrayField } from './field-array';
import { DynamicFormFieldsProps } from './types';
import { FormCsvLookupField } from './csv-lookup';
import { FormScaleRatingField } from './scale-rating';

export const DynamicFormFields = ({
  fields,
  container,
  hideLabels,
}: DynamicFormFieldsProps) => {
  const items = useMemo(() => {
    // Static content owns its full-width layout and bypasses question wrappers.
    let wrappedFieldIndex = 0;
    return fields.map((field, index) => {
      let fieldNode: ReactNode;
      const props = { ...field };
      if (hideLabels && props.type !== FormFieldType.HEADING) {
        props.label = '';
      }
      switch (props.type) {
        case FormFieldType.INPUT:
          fieldNode = <FormInputField key={props.id} {...props} />;
          break;
        case FormFieldType.SELECT:
          fieldNode = <FormSelectField key={props.id} {...props} />;
          break;
        case FormFieldType.RADIO:
          fieldNode = <FormRadioField key={props.id} {...props} />;
          break;
        case FormFieldType.CHECKBOX:
          fieldNode = <FormCheckboxField key={props.id} {...props} />;
          break;
        case FormFieldType.SCALE_RATING:
          fieldNode = <FormScaleRatingField key={props.id} {...props} />;
          break;
        case FormFieldType.OUTCOMES:
          fieldNode = (
            <div
              key={props.id}
              className="rounded border border-dashed border-gray-300 p-4 text-sm text-gray-600"
            >
              Outcomes are resolved and ordered in the participant app.
            </div>
          );
          break;
        case FormFieldType.OUTCOME_FIELDS:
          fieldNode = (
            <div
              key={props.id}
              className="flex flex-col gap-4 rounded border border-dashed border-gray-300 p-4 text-sm text-gray-600"
            >
              <div>
                Outcome fields repeat for each resolved outcome in the
                participant app.
              </div>
              {typeof props.options?.maxOutcomeCount === 'number' && (
                <div>
                  Only the first {props.options.maxOutcomeCount} outcomes will
                  require input.
                </div>
              )}
              {!!props.options?.fields?.length && (
                <div className="rounded border border-gray-300 p-4">
                  <DynamicFormFields fields={props.options.fields} />
                </div>
              )}
            </div>
          );
          break;
        case FormFieldType.FIELD_ARRAY:
          fieldNode = <FieldArrayField key={props.id} {...props} />;
          break;
        case FormFieldType.CSV_LOOKUP:
          fieldNode = <FormCsvLookupField key={props.id} {...props} />;
          break;
        case FormFieldType.PAGE_BREAK:
          fieldNode = (
            <div className="relative my-6 flex items-center">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="mx-4 rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700">
                Page Break
              </span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>
          );
          break;
        case FormFieldType.TEXT:
          fieldNode = <FormText key={props.id} {...props} />;
          break;
        case FormFieldType.HEADING:
          fieldNode = <FormHeading key={props.id} {...props} />;
          break;
        default:
          fieldNode = <Fragment key={index} />;
          break;
      }
      if (
        field.type === FormFieldType.HEADING ||
        field.type === FormFieldType.TEXT
      ) {
        return fieldNode;
      }

      const containerIndex = wrappedFieldIndex;
      wrappedFieldIndex += 1;
      return container
        ? container(field, fieldNode, containerIndex)
        : fieldNode;
    });
  }, [fields, container, hideLabels]);

  return <>{items}</>;
};
